/**
 * dscCascade.js
 *
 * Pure-function cascade reducer for the DSC. Translates the long
 * `manageDSCFilteringData(evt)` if/else chain from
 * `searchResults.js:1206-1729` into a stateless reducer:
 *
 *     dscCascadeReducer(state, action) → newState
 *
 * Source-of-truth branches (legacy line ranges):
 *  - reduceShankSlot         lines 1240-1318   (handle shank-adapter changes)
 *  - reduceCouplingSlot      lines 1319-1407   (handle coupling type / thread changes)
 *  - reduceRodSlot           lines 1408-1430   (handle rod thread change)
 *  - reduceDrillBitSlot      lines 1431-1447   (handle bit type change)
 *  - reduceDTHBitSlot        lines 1448-1548   (DTH bit-first cascade)
 *  - reduceDTHHammerSlot     lines 1549-1722   (DTH hammer-first cascade + reverseFilter)
 *  - reduceDTHDrillBitSlot   lines 1723-1729+  (DTH drill-bit cascade — terminal)
 *  - reduceBitAdapterSlot    NEW per brainstorm §A — no legacy source
 *
 * Toggle handlers (NOT in the legacy reducer — these are PWA additions
 * required because the brainstorm wishlist #15 + plan §1.5 calls for
 * **preserving compatible selections** across toggle flips, fixing the
 * legacy bug where `handleCouplingSelection` calls
 * `handleRestartConfigurator()` first (`searchResults.js:1200-1204`):
 *  - reduceToggleChange      preserves shank-adapter selection across
 *                            coupling / bit-adapter toggle flips; resets
 *                            ONLY downstream slots whose cascade chain
 *                            branches differently.
 *
 * RESTART:
 *  - reduceRestart           legacy `handleRestartConfigurator` (no line
 *                            range — fires from `handleCouplingSelection`
 *                            and the user "Restart configurator" button).
 *
 * Each reducer is PURE: it neither reads nor writes external state. The
 * options it computes come from the `state.dscFilteringData` and
 * `state.dscDTHHammerData` master lists (seeded by `dscFilteringData.js`)
 * + the `state.products` array (passed in for thread lookups via
 * dscMatchingService).
 *
 * @module services/dscCascade
 * @since 2026-05-20 (Group E SP1.4)
 */

import {
  getCouplingThreadforRods,
  getCouplingThreadforBits,
  getThreadInformation,
} from './dscMatchingService';
import { deriveFromRockDrill } from './equipmentMatcher';

// ============================================================================
// Action types
// ============================================================================

export const DSC_ACTIONS = {
  SET_SELECTION: 'SET_SELECTION',
  SET_DROPDOWN_CHOICE: 'SET_DROPDOWN_CHOICE',
  TOGGLE: 'TOGGLE',
  SET_PRODUCT_LINE: 'SET_PRODUCT_LINE',
  CLEAR_SELECTION: 'CLEAR_SELECTION',
  RESTART: 'RESTART',
  BOOTSTRAP: 'BOOTSTRAP',
};

// ============================================================================
// Helpers
// ============================================================================

/** Defensive clone of the selections + dropdownChoices subtrees. */
function clone(state) {
  return {
    ...state,
    toggles: { ...state.toggles },
    selections: { ...state.selections },
    dropdownChoices: { ...state.dropdownChoices },
    dropdownOptions: { ...state.dropdownOptions },
  };
}

function setDropdownOptions(state, slotId, options) {
  state.dropdownOptions[slotId] = options;
}

function clearSlot(state, slotId) {
  delete state.selections[slotId];
  delete state.dropdownChoices[slotId];
}

/**
 * Build unique-keyed `{label,value}` options sorted alphabetically.
 * Equivalent to legacy `new Set(...).forEach(...)` + `sort` blocks.
 */
function uniqueLabelValueOptions(values, { withSelect = true, numericSort = false } = {}) {
  const set = new Set();
  for (const v of values) {
    if (v == null || v === '') continue;
    set.add(v);
  }
  let arr = [...set].map((v) => ({ label: String(v), value: String(v) }));
  if (numericSort) {
    arr.sort((a, b) => {
      const an = parseFloat(a.label);
      const bn = parseFloat(b.label);
      if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
      return a.label.localeCompare(b.label);
    });
  } else {
    arr.sort((a, b) => a.label.localeCompare(b.label));
  }
  return withSelect ? [{ label: 'Select', value: 'None' }, ...arr] : arr;
}

// ============================================================================
// TH cascade reducers
// ============================================================================

/**
 * Reduce a SET_SELECTION on 'shank-adapter'.
 *
 * Legacy: searchResults.js:1240-1318.
 * Three sub-cases in the legacy if-chain (line 1241 / 1271 / 1316):
 *  (a) selectedManufacture change   (1241-1270)
 *  (b) selectedDrillModel change    (1271-1315)
 *  (c) selectedItemCode change      (1316-1317)
 */
export function reduceShankSlot(state, action) {
  const next = clone(state);
  const { dropdownKey, value } = action;
  const filteringData = state.dscFilteringData || [];

  // (a) Manufacturer changed → reset model / itemCode / downstream slots.
  if (dropdownKey === 'manufacture') {
    next.selections['shank-adapter'] = { mfr: value === 'None' ? null : value };
    next.dropdownChoices['shank-adapter'] = { manufacture: value };

    // Build model options for the new manufacturer (lines 1242-1252).
    const models = filteringData
      .filter((item) => item.manufacture === value && item.compatibleRockDrill)
      .map((item) => item.compatibleRockDrill);
    setDropdownOptions(next, 'shank-adapter:model', uniqueLabelValueOptions(models));

    // Reset downstream slots (lines 1255-1265).
    clearSlot(next, 'rod');
    clearSlot(next, 'drill-bit');
    if (next.toggles.coupling) clearSlot(next, 'couplings');
    if (next.toggles.bitAdapter) clearSlot(next, 'bit-adapter');
    return next;
  }

  // (b) Drill model changed → reset itemCode + thread options downstream.
  if (dropdownKey === 'model') {
    const prevSel = next.selections['shank-adapter'] || {};
    next.selections['shank-adapter'] = { ...prevSel, model: value === 'None' ? null : value };
    next.dropdownChoices['shank-adapter'] = {
      ...(next.dropdownChoices['shank-adapter'] || {}),
      model: value,
    };

    // Lines 1287-1293 — itemCode options.
    const itemCodes = filteringData
      .filter(
        (item) =>
          item.manufacture === prevSel.mfr &&
          item.compatibleRockDrill === value &&
          item.itemCode
      )
      .map((item) => item.itemCode);
    setDropdownOptions(next, 'shank-adapter:itemCode', uniqueLabelValueOptions(itemCodes));

    // Derive rod-thread options per the TH PPT logic (and Vandana's
    // 20 May 2026 confirmation): "selecting the rock drill in our top
    // hammer shank adapters .. you will get a set of sandvik shank adapters
    // and from those you are to select distinct threads of those shank
    // adapters to populate the thread system drop down."
    //
    // i.e. getThreadInformation(mfr, model, products, pgmj=184, pgmn='T20')
    // — filter shank adapters by the picked rock-drill manufacturer +
    // model, return distinct Thread__c values.
    //
    // KNOWN DATA-COMPLETENESS GAP (PWA products-tophammer.json):
    //   - Epiroc: only 6 products with rockDrillManufacturer='Epiroc',
    //     none carry COP* in compatibleRockDrill. The data ingestion
    //     dropped the Excel's competitor-to-Sandvik-shank mappings for
    //     Epiroc COP series. Mfr+model dropdowns show the correct catalog
    //     (rockDrillManufacturers.json), but cascade returns empty for
    //     COP picks until the data is re-ingested.
    //   - Other competitors (Montabert, Furukawa, Gardner-Denver,
    //     Ingersoll-Rand) appear properly populated.
    const products = state.products || [];
    const targetModel = value === 'None' ? null : value;
    if (prevSel.mfr && targetModel) {
      const rodThreads = getThreadInformation(
        prevSel.mfr,
        targetModel,
        products,
        184,
        'T20'
      );
      setDropdownOptions(next, 'rod:thread', uniqueLabelValueOptions(rodThreads));
    } else {
      // Model deselected ('None') — clear downstream options.
      setDropdownOptions(next, 'rod:thread', []);
    }

    // Reset downstream slot selections (lines 1296-1306).
    clearSlot(next, 'rod');
    clearSlot(next, 'drill-bit');
    if (next.toggles.coupling) clearSlot(next, 'couplings');
    if (next.toggles.bitAdapter) clearSlot(next, 'bit-adapter');
    return next;
  }

  // (c) Item code chosen — leaf selection within shank-adapter (line 1316-1317).
  if (dropdownKey === 'itemCode') {
    const prevSel = next.selections['shank-adapter'] || {};
    next.selections['shank-adapter'] = { ...prevSel, itemCode: value === 'None' ? null : value };
    return next;
  }

  return next;
}

/**
 * Reduce a SET_SELECTION on 'couplings'.
 *
 * Legacy: searchResults.js:1319-1407.
 * Two sub-cases:
 *  (a) couplingType change          (1320-1365) — calls getCouplingThreadforRods
 *  (b) thread-for-rod change        (1366-1406) — calls getCouplingThreadforBits
 *
 * Both legacy branches are async (returned a Promise from Apex); here the
 * matching service is synchronous so we resolve inline.
 */
export function reduceCouplingSlot(state, action) {
  const next = clone(state);
  const { dropdownKey, value } = action;
  const products = state.products || [];
  const shankSel = state.selections['shank-adapter'] || {};

  if (dropdownKey === 'couplingType') {
    const couplingResult = getCouplingThreadforRods(
      shankSel.mfr,
      shankSel.model,
      value,
      products,
      184, // legacy passes '184'
      'T24',
      { entityCode: state.entityCode || null }
    );
    next.selections.couplings = {
      type: value === 'None' ? null : value,
      rodThread: null,
    };
    next.dropdownChoices.couplings = { couplingType: value };

    // Lines 1349 + 1355 + 1359 — store options for the downstream dropdowns.
    setDropdownOptions(
      next,
      'couplings:threadForRod',
      uniqueLabelValueOptions(couplingResult.thThreadF2)
    );
    setDropdownOptions(
      next,
      'rod:thread',
      uniqueLabelValueOptions(couplingResult.thThread1)
    );
    next.dropdownOptions['rod:shankThreadAvailable'] = couplingResult.thThread;

    // Lines 1330-1334 — rod + drill-bit reset.
    clearSlot(next, 'rod');
    clearSlot(next, 'drill-bit');
    return next;
  }

  if (dropdownKey === 'threadForRod') {
    const prev = next.selections.couplings || {};
    next.selections.couplings = { ...prev, rodThread: value === 'None' ? null : value };
    next.dropdownChoices.couplings = {
      ...(next.dropdownChoices.couplings || {}),
      threadForRod: value,
    };

    // Lines 1373-1382 — when 'None', use all thread2 values; else just the one.
    let rodThread2 = [];
    if (value === 'None') {
      const allOpts = next.dropdownOptions['couplings:threadForRod'] || [];
      rodThread2 = allOpts.map((o) => o.value).filter((v) => v !== 'None');
    } else {
      rodThread2 = [value];
    }
    const bitThreads = getCouplingThreadforBits(rodThread2, products, 182, {
      entityCode: state.entityCode || null,
    });
    setDropdownOptions(next, 'rod:thread', uniqueLabelValueOptions(bitThreads));
    clearSlot(next, 'drill-bit');
    return next;
  }

  return next;
}

/**
 * Reduce a SET_SELECTION on 'rod'.
 *
 * Legacy: searchResults.js:1408-1430.
 *
 * On rod thread chosen:
 *  - If coupling toggle ON: defer to `drillBitOptionForCoupling()` (legacy
 *    sibling helper). Here that branch lists button-shapes via
 *    getCouplingButtonShape against the chosen thread — to keep this reducer
 *    pure, the host calls dscMatchingService.getCouplingButtonShape and
 *    passes the result via action payload OR (simpler default) we let the
 *    cascade compute it ourselves below.
 *  - If coupling toggle OFF: button-shape from filteringData filtered on
 *    (mfr, model, thread).
 */
export function reduceRodSlot(state, action) {
  const next = clone(state);
  const { value } = action;
  const filteringData = state.dscFilteringData || [];
  const products = state.products || [];
  const shankSel = state.selections['shank-adapter'] || {};
  const rodThread = value === 'None' ? null : value;

  next.selections.rod = { thread: rodThread };
  next.dropdownChoices.rod = { thread: value };

  // Lines 1417-1421 — when no coupling, source buttonShapes from filteringData.
  if (!state.toggles.coupling) {
    const buttonShapes = filteringData
      .filter(
        (item) =>
          item.manufacture === shankSel.mfr &&
          item.compatibleRockDrill === shankSel.model &&
          item.thread === value &&
          item.buttonShape &&
          item.buttonShape !== 'N/A'
      )
      .map((item) => item.buttonShape);
    setDropdownOptions(next, 'drill-bit:buttonShape', uniqueLabelValueOptions(buttonShapes));
  }
  // When coupling toggle is ON, the calling code is expected to dispatch a
  // follow-up action with the bit-button-shape options computed via
  // dscMatchingService.getCouplingButtonShape (legacy
  // `drillBitOptionForCoupling()` did this asynchronously).

  // Populate bit-adapter:thread when bit-adapter toggle is ON.
  if (next.toggles.bitAdapter) {
    setDropdownOptions(
      next,
      'bit-adapter:thread',
      uniqueLabelValueOptions(computeBitAdapterThreadOptions(products, rodThread))
    );
  }

  clearSlot(next, 'drill-bit');
  clearSlot(next, 'bit-adapter');
  return next;
}

/**
 * Distinct "other end" threads of bit adapters compatible with the picked
 * rod thread. Used by reduceRodSlot AND reduceToggleChange so the
 * bit-adapter:thread dropdown stays correct regardless of which user action
 * comes first (rod-thread pick vs. bit-adapter toggle flip).
 *
 * Matching rule (Vandana, 20 May 2026 walkthrough):
 *   - Restrict to pilot-bit-adapters whose description includes 'BIT ADAPTER'
 *     (excludes guide adapters + barrel sleeves which share the same
 *     subcategory).
 *   - A bit adapter matches the rod thread when EITHER the description-
 *     parsed ends OR an explicit thread field carries the rod thread.
 *   - The dropdown shows the OTHER end (the bit-side thread).
 */
function computeBitAdapterThreadOptions(products, rodThread) {
  if (!Array.isArray(products) || !rodThread) return [];
  const out = new Set();
  for (const p of products) {
    if (p?.subcategory !== 'pilot-bit-adapters') continue;
    const desc = String(p?.description || '').toUpperCase();
    if (!desc.includes('BIT ADAPTER')) continue;
    let ends = [];
    const m = String(p.description || '').match(/BIT ADAPTER\s+(\S+)/i);
    if (m && m[1].includes('/')) {
      ends = m[1]
        .split('/')
        .map((s) => s.replace(/[FM]$/i, '').trim())
        .filter(Boolean);
    }
    const matched = ends.includes(rodThread)
      || p.thread === rodThread
      || p.thread1 === rodThread
      || p.thread2 === rodThread;
    if (!matched) continue;
    const other = ends.find((t) => t !== rodThread)
      || (p.thread && p.thread !== rodThread ? p.thread : null);
    if (other) out.add(other);
  }
  return [...out];
}

/**
 * Reduce a SET_SELECTION on 'drill-bit'.
 *
 * Legacy: searchResults.js:1431-1447.
 * Pure leaf selection — no downstream slot changes (no further cascade).
 */
export function reduceDrillBitSlot(state, action) {
  const next = clone(state);
  const { value, dropdownKey = 'type' } = action;
  const prev = next.selections['drill-bit'] || {};
  next.selections['drill-bit'] = { ...prev, [dropdownKey]: value === 'None' ? null : value };
  next.dropdownChoices['drill-bit'] = {
    ...(next.dropdownChoices['drill-bit'] || {}),
    [dropdownKey]: value,
  };
  return next;
}

/**
 * Reduce a SET_SELECTION on 'bit-adapter'  (NEW slot — no legacy source).
 *
 * Brainstorm §A locked the slot insertion between Rods and Bits. No legacy
 * cascade behaviour; we treat bit-adapter as a leaf-ish slot. When the user
 * picks an adapter thread, we DO reset the downstream 'drill-bit' slot so
 * the bit thread can be re-picked compatible with the new adapter thread —
 * mirrors the legacy rod→drill-bit reset pattern (line 1425).
 */
export function reduceBitAdapterSlot(state, action) {
  const next = clone(state);
  const { value, dropdownKey = 'thread' } = action;
  const prev = next.selections['bit-adapter'] || {};
  next.selections['bit-adapter'] = { ...prev, [dropdownKey]: value === 'None' ? null : value };
  next.dropdownChoices['bit-adapter'] = {
    ...(next.dropdownChoices['bit-adapter'] || {}),
    [dropdownKey]: value,
  };
  clearSlot(next, 'drill-bit');
  return next;
}

// ============================================================================
// DTH cascade reducers
// ============================================================================

/**
 * Reduce a SET_SELECTION on 'dth-bit'  (bit-first entry — `reverseFilter=false`).
 *
 * Legacy: searchResults.js:1448-1548.
 *
 * On bit diameter chosen (and reverseFilter false):
 *  - Build hammerShankSize map: shankType → shankSize for items matching bit.
 *    (lines 1454-1475)
 *  - Build dthHammerOptions: distinct shankType for items matching bit.
 *    (lines 1477-1498)
 *  - When value === 'None', restore the full hammer-family options list
 *    (lines 1501-1525).
 *  - When value !== 'None', set dthHammerOptions + clear family list
 *    (lines 1523-1526).
 *  - Reset hammer-series + drillpipe selections (lines 1527-1531).
 */
export function reduceDTHBitSlot(state, action) {
  const next = clone(state);
  const { value } = action;
  const filteringData = state.dscFilteringData || [];
  const dthHammerData = state.dscDTHHammerData || [];
  const reverseFilter = !!(state.selections['hammer-series']?.family);

  next.selections['dth-bit'] = { holeSize: value === 'None' ? null : value };
  next.dropdownChoices['dth-bit'] = { holeSize: value };

  if (!reverseFilter) {
    // Lines 1454-1475 — hammerShankSize map (shankType → shankSize).
    const shankSizeMap = new Map();
    for (const item of filteringData) {
      const isMatch =
        (item.bitDiameter === value && item.shankType) ||
        value === 'None';
      if (!isMatch) continue;
      if (!shankSizeMap.has(item.shankType)) {
        shankSizeMap.set(item.shankType, item.shankSize);
      }
    }
    // Lines 1477-1496 — shankType options (the "DTH Hammer" sub-dropdown).
    const shankTypeMap = new Map();
    for (const item of filteringData) {
      const isMatch =
        (item.bitDiameter === value && item.shankType) ||
        value === 'None';
      if (!isMatch) continue;
      if (!shankTypeMap.has(item.shankType)) {
        shankTypeMap.set(item.shankType, item.bitShankType);
      }
    }
    const shankTypeOptions = uniqueLabelValueOptions([...shankTypeMap.keys()]);

    if (value === 'None') {
      // Lines 1501-1522 — restore full hammer-family list.
      const familyMap = new Map();
      for (const item of dthHammerData) {
        const label = `${item.hammerFamily}-${item.hammerSizeInch}`;
        if (!familyMap.has(item.hammerFamily)) familyMap.set(item.hammerFamily, label);
      }
      const familyOptions = uniqueLabelValueOptions([...familyMap.values()]);
      setDropdownOptions(next, 'hammer-series:family', familyOptions);
      // Key alignment fix (20 May): component reads slot.options.shank,
      // not .shankType. Cascade must write under :shank to match.
      setDropdownOptions(next, 'hammer-series:shank', []);
    } else {
      // Lines 1524-1525 — set shank-type options, clear family list.
      setDropdownOptions(next, 'hammer-series:shank', shankTypeOptions);
      setDropdownOptions(next, 'hammer-series:family', []);
    }

    // Lines 1527-1531 — reset hammer-series + drillpipe.
    clearSlot(next, 'hammer-series');
    clearSlot(next, 'drillpipe');
    next.dropdownOptions['hammer-series:shankSizeMap'] = [...shankSizeMap.entries()].map(
      ([key, val]) => ({ label: key, value: val })
    );
  }
  return next;
}

/**
 * Reduce a SET_SELECTION on 'hammer-series'.
 *
 * Legacy: searchResults.js:1549-1722.
 *
 * Two sub-cases:
 *  (a) selectedDTHHammer (shankType) change      (1551-1607)
 *  (b) selectedDTHHammerFamily change            (1608-1722) — sets reverseFilter=true
 *
 * The 'family' branch is the **hammer-first entry point** per brainstorm DTH #1.
 */
export function reduceDTHHammerSlot(state, action) {
  const next = clone(state);
  const { dropdownKey, value } = action;
  const filteringData = state.dscFilteringData || [];
  const dthHammerData = state.dscDTHHammerData || [];

  // (a) Sub-dropdown "shank type for the Bit" (selectedDTHHammer) — lines 1551-1607.
  if (dropdownKey === 'shank' || dropdownKey === 'shankType') {
    const prev = next.selections['hammer-series'] || {};
    next.selections['hammer-series'] = { ...prev, shank: value === 'None' ? null : value };
    next.dropdownChoices['hammer-series'] = {
      ...(next.dropdownChoices['hammer-series'] || {}),
      shank: value,
    };

    // Lines 1558-1564 — pin options from dscDTHHammerData filtered on shankType.
    // Key alignment fix (20 May): DTHDrillpipeDropdowns reads slot.options.pinOption,
    // so cascade must write to 'drillpipe:pinOption' (not 'drillpipe:pin').
    const pins = dthHammerData
      .filter((item) => item.shankType === value && item.pin)
      .map((item) => item.pin);
    setDropdownOptions(next, 'drillpipe:pinOption', uniqueLabelValueOptions(pins));

    // Lines 1571-1595 — reverse-filter mode: if family already chosen, re-derive
    // bit options compatible with the picked shankType.
    const family = prev.family;
    if (family && value !== 'None') {
      const bitMap = new Map();
      for (const item of filteringData) {
        if (item.shankType === value && item.bitDiameter) {
          if (!bitMap.has(item.bitDiameter)) {
            // Legacy line 1578: label is mm by default (checkboxVal=false).
            bitMap.set(item.bitDiameter, item.bitDiameterMM ?? item.bitDiameter);
          }
        }
      }
      const bitOptions = [
        { label: 'Select', value: 'None' },
        ...[...bitMap.entries()].map(([v, label]) => ({
          label: String(label),
          value: String(v),
        })),
      ];
      setDropdownOptions(next, 'dth-bit:holeSize', bitOptions);
    } else if (family && value === 'None') {
      setDropdownOptions(next, 'dth-bit:holeSize', []);
      clearSlot(next, 'dth-bit');
    }

    clearSlot(next, 'drillpipe');
    return next;
  }

  // (b) Top dropdown "Hammer Series" (selectedDTHHammerFamily) — lines 1608-1722.
  // Hammer-first entry — sets reverseFilter=true and reverse-derives bit + pin
  // options.
  if (dropdownKey === 'family' || dropdownKey === 'hammerFamily') {
    const familyValue = value;
    next.selections['hammer-series'] = {
      family: familyValue === 'None' ? null : familyValue,
      size: null,
      shank: null,
    };
    next.dropdownChoices['hammer-series'] = { family: familyValue };

    if (familyValue === 'None' || familyValue == null) {
      // Lines 1632-1657 — reset to bit-first mode.
      setDropdownOptions(next, 'hammer-series:shankType', []);
      clearSlot(next, 'hammer-series');
      // Restore full bit-diameter list (line 1640-1655).
      const bitMap = new Map();
      for (const item of filteringData) {
        if (!bitMap.has(item.bitDiameter)) {
          bitMap.set(item.bitDiameter, item.bitDiameterMM ?? item.bitDiameter);
        }
      }
      const bitOptions = [
        { label: 'Select', value: 'None' },
        ...[...bitMap.entries()].map(([v, label]) => ({
          label: String(label),
          value: String(v),
        })),
      ];
      setDropdownOptions(next, 'dth-bit:holeSize', bitOptions);
      clearSlot(next, 'drillpipe');
      return next;
    }

    // Lines 1612-1631 — split family label `RH560-3.5` → [family, size].
    const [familyName, familySize] = String(familyValue).split('-');

    // Lines 1616-1628 — shankType options where hammerFamily && hammerSizeInch match.
    const shankTypeMap = new Map();
    for (const item of dthHammerData) {
      if (item.hammerFamily === familyName && item.hammerSizeInch === familySize && item.shankType) {
        if (!shankTypeMap.has(item.shankType)) shankTypeMap.set(item.shankType, item.shankType);
      }
    }
    const shankTypeOptions = [...shankTypeMap.keys()].map((k) => ({ label: k, value: k }));
    // Key alignment fix (20 May): write to :shank to match component read.
    setDropdownOptions(next, 'hammer-series:shank', shankTypeOptions);

    // UX improvement (21 May, tracker #20): when the hammer family yields
    // exactly one shank type (e.g. RH570-5.5 -> 570-5.5), auto-select it
    // so the UI renders the dropdown read-only. Eliminates a redundant click.
    if (shankTypeOptions.length === 1) {
      const autoShank = shankTypeOptions[0].value;
      next.selections['hammer-series'].shank = autoShank;
      next.selections['hammer-series'].shankAutoSelected = true;
      next.dropdownChoices['hammer-series'] = {
        ...(next.dropdownChoices['hammer-series'] || {}),
        shank: autoShank,
      };
      // Derive pin options for the auto-selected shank.
      const pins = dthHammerData
        .filter((item) => item.shankType === autoShank && item.pin)
        .map((item) => item.pin);
      setDropdownOptions(next, 'drillpipe:pinOption', uniqueLabelValueOptions(pins));
    }

    // Lines 1659-1707 — reverse-filter mode: build dth-bit + drillpipe options
    // by union of all shankTypes in the family.
    const shankValues = shankTypeOptions.map((o) => o.value);
    const bitMap = new Map();
    for (const item of filteringData) {
      if (shankValues.includes(item.shankType) && item.bitDiameter) {
        if (!bitMap.has(item.bitDiameter)) {
          bitMap.set(item.bitDiameter, item.bitDiameterMM ?? item.bitDiameter);
        }
      }
    }
    const bitOptions = [
      { label: 'Select', value: 'None' },
      ...[...bitMap.entries()].map(([v, label]) => ({
        label: String(label),
        value: String(v),
      })),
    ];
    setDropdownOptions(next, 'dth-bit:holeSize', bitOptions);

    const pinMap = new Map();
    for (const item of dthHammerData) {
      if (shankValues.includes(item.shankType) && item.pin) {
        if (!pinMap.has(item.pin)) pinMap.set(item.pin, item.pin);
      }
    }
    const pinOptions = [
      { label: 'Select', value: 'None' },
      ...[...pinMap.keys()].map((k) => ({ label: k, value: k })),
    ];
    // Key alignment fix (20 May): write to :pinOption to match component read.
    setDropdownOptions(next, 'drillpipe:pinOption', pinOptions);

    // Apply the family.size from the split label, mark reverseFilter implicitly
    // (presence of family in selections).
    next.selections['hammer-series'].size = familySize || null;

    clearSlot(next, 'dth-bit');
    clearSlot(next, 'drillpipe');
    return next;
  }

  return next;
}

/**
 * Reduce a SET_SELECTION on 'drillpipe'.
 *
 * Legacy: searchResults.js:1723-1729 — terminal leaf, no further cascade.
 */
export function reduceDTHDrillBitSlot(state, action) {
  const next = clone(state);
  const { value } = action;
  next.selections.drillpipe = { pinOption: value === 'None' ? null : value };
  next.dropdownChoices.drillpipe = { pin: value };
  return next;
}

// ============================================================================
// Toggle change — PWA improvement vs legacy bug
// ============================================================================

/**
 * Toggle (coupling / bit-adapter / unit / future) flip.
 *
 * LEGACY BUG (`searchResults.js:1199-1204` `handleCouplingSelection`):
 *   calls `handleRestartConfigurator()` BEFORE re-rendering — wipes
 *   everything including the shank-adapter selection. Plan §1.5 +
 *   brainstorm Q5 + wishlist #15 require this be fixed: shank-adapter
 *   selections SURVIVE every toggle flip; only downstream selections
 *   whose cascade chain branches differently are reset.
 *
 * Behaviour matrix:
 *   coupling ON  → preserve shank-adapter; insert empty couplings slot;
 *                  rod + drill-bit reset because their thread cascade now
 *                  routes through getCouplingThreadforRods instead of
 *                  getThreadInformation.
 *   coupling OFF → preserve shank-adapter; remove couplings slot; rod +
 *                  drill-bit reset for same reason (cascade returns to the
 *                  direct getThreadInformation path).
 *   bitAdapter ON  → preserve shank-adapter, couplings, rod; insert empty
 *                    bit-adapter slot between rod and drill-bit; drill-bit
 *                    reset because its thread now depends on adapter thread.
 *   bitAdapter OFF → preserve shank-adapter, couplings, rod; remove
 *                    bit-adapter slot; drill-bit reset.
 *   unitImperial   → DTH-only, NO selection reset. Bit-diameter labels in
 *                    dropdownOptions are recomputed when the host triggers a
 *                    BOOTSTRAP — see plan §SP3.4. Selections preserved because
 *                    both bitDiameterMM and bitDiameterInch are kept in
 *                    masterFilteringData rows.
 *   other          → arbitrary future toggles (shock absorber, stabiliser,
 *                    sampler tube). Default behaviour: preserve everything.
 *                    Slot insertion handled by `dscSlotConfig.js`.
 */
export function reduceToggleChange(state, action) {
  const next = clone(state);
  const { toggleKey, value } = action;
  next.toggles[toggleKey] = !!value;

  if (toggleKey === 'coupling') {
    // Preserve shank-adapter selection. Reset everything downstream of it.
    clearSlot(next, 'couplings');
    clearSlot(next, 'rod');
    clearSlot(next, 'drill-bit');
    // Coupling OFF (task #16): rod-thread cascade returns to the direct
    // getThreadInformation(shank → thread) path. Re-seed rod:thread from
    // the preserved shank-adapter mfr+model so the user doesn't have to
    // reselect them. Symmetric with the bit-adapter OFF branch below.
    // (Coupling ON needs no seeding — reduceCouplingSlot('couplingType')
    // repopulates rod:thread via getCouplingThreadforRods when the user
    // picks a coupling type.)
    if (!value) {
      const shank = next.selections['shank-adapter'] || {};
      if (shank.mfr && shank.model) {
        const rodThreads = getThreadInformation(
          shank.mfr,
          shank.model,
          state.products || [],
          184,
          'T20'
        );
        setDropdownOptions(next, 'rod:thread', uniqueLabelValueOptions(rodThreads));
      } else {
        setDropdownOptions(next, 'rod:thread', []);
      }
    }
    return next;
  }
  if (toggleKey === 'bitAdapter') {
    // Preserve shank-adapter, couplings, rod. Reset bit-adapter + drill-bit.
    clearSlot(next, 'bit-adapter');
    clearSlot(next, 'drill-bit');
    // Populate bit-adapter:thread dropdown when toggling ON and rod thread
    // is already picked. Without this, the dropdown stays empty until the
    // user re-picks the rod thread (which they shouldn't have to).
    if (value && next.selections.rod?.thread) {
      setDropdownOptions(
        next,
        'bit-adapter:thread',
        uniqueLabelValueOptions(
          computeBitAdapterThreadOptions(state.products || [], next.selections.rod.thread)
        )
      );
    } else if (value) {
      setDropdownOptions(next, 'bit-adapter:thread', uniqueLabelValueOptions([]));
    }
    return next;
  }
  if (toggleKey === 'unitImperial') {
    // DTH unit flip — preserve everything. Host re-runs BOOTSTRAP to refresh
    // bit-diameter labels with the new unit.
    return next;
  }
  // Future toggles — preserve everything by default.
  return next;
}

// ============================================================================
// RESTART
// ============================================================================

/**
 * Restart the configurator. Clears all selections + dropdownChoices but
 * preserves the master filtering data and the current productLine.
 */
export function reduceRestart(state) {
  return {
    ...state,
    selections: {},
    dropdownChoices: {},
    dropdownOptions: { ...(state.initialDropdownOptions || {}) },
  };
}

// ============================================================================
// BOOTSTRAP — seed dropdownOptions with the initial-options map.
// ============================================================================

export function reduceBootstrap(state, action) {
  const {
    initialOptions,
    filteringData,
    dthHammerData,
    products,
    rockDrillManufacturers,
    rockDrills,
  } = action;
  return {
    ...state,
    // products MUST be merged here — DSCProvider's initialState is mount-only
    // memoized; without this the state.products stays empty even after
    // loadAllData async-resolves. (Bug surfaced 20 May 2026 SP6 walkthrough.)
    products: Array.isArray(products) && products.length > 0
      ? products
      : (state.products ?? []),
    // Rock-drill catalog data — same source as the Replace path
    // (StepEquipmentSelect). DSC TH shank mfr+model dropdowns source from
    // these; reduceShankSlot uses deriveFromRockDrill to map a picked drill
    // model to its threadSystem (then populates the rod:thread dropdown).
    rockDrillManufacturers: rockDrillManufacturers ?? state.rockDrillManufacturers ?? null,
    rockDrills: rockDrills ?? state.rockDrills ?? null,
    dscFilteringData: filteringData ?? state.dscFilteringData ?? [],
    dscDTHHammerData: dthHammerData ?? state.dscDTHHammerData ?? [],
    dropdownOptions: { ...initialOptions },
    initialDropdownOptions: { ...initialOptions },
  };
}

// ============================================================================
// CLEAR_SELECTION — single-slot reset
// ============================================================================

export function reduceClearSelection(state, action) {
  const next = clone(state);
  clearSlot(next, action.slotId);
  return next;
}

// ============================================================================
// Master switch
// ============================================================================

export function dscCascadeReducer(state, action) {
  if (!action || !action.type) return state;
  switch (action.type) {
    case DSC_ACTIONS.SET_SELECTION:
    case DSC_ACTIONS.SET_DROPDOWN_CHOICE:
      switch (action.slotId) {
        case 'shank-adapter':  return reduceShankSlot(state, action);
        case 'couplings':      return reduceCouplingSlot(state, action);
        case 'rod':            return reduceRodSlot(state, action);
        case 'drill-bit':      return reduceDrillBitSlot(state, action);
        case 'bit-adapter':    return reduceBitAdapterSlot(state, action);
        case 'dth-bit':        return reduceDTHBitSlot(state, action);
        case 'hammer-series':  return reduceDTHHammerSlot(state, action);
        case 'drillpipe':      return reduceDTHDrillBitSlot(state, action);
        default:               return state;
      }
    case DSC_ACTIONS.TOGGLE:        return reduceToggleChange(state, action);
    case DSC_ACTIONS.CLEAR_SELECTION:return reduceClearSelection(state, action);
    case DSC_ACTIONS.RESTART:       return reduceRestart(state);
    case DSC_ACTIONS.BOOTSTRAP:     return reduceBootstrap(state, action);
    case DSC_ACTIONS.SET_PRODUCT_LINE:
      return {
        ...reduceRestart(state),
        productLine: action.value,
        dscFilteringData: [],
        dscDTHHammerData: [],
      };
    default: return state;
  }
}

// ============================================================================
// Initial state factory
// ============================================================================

export function makeInitialDSCState(productLine, overrides = {}) {
  return {
    productLine,
    toggles: productLine === 'TH'
      ? { coupling: false, bitAdapter: false }
      : { unitImperial: false },
    selections: {},
    dropdownChoices: {},
    dropdownOptions: {},
    initialDropdownOptions: {},
    dscFilteringData: [],
    dscDTHHammerData: [],
    products: [],
    entityCode: null,
    ...overrides,
  };
}